import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook to fetch video events (NIP-71) for a specific pubkey
 * Supports both current (34235/34236) and legacy (21/22) kinds
 */
export function useVideos(pubkey: string | undefined, enabled = true) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['videos', pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const events = await nostr.query([
        {
          kinds: [34235, 34236, 21, 22], // Current and legacy video kinds
          authors: [pubkey],
          limit: 50,
        }
      ], { signal });

      // Filter valid video events
      return events
        .filter(validateVideoEvent)
        .sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!pubkey && enabled,
    staleTime: 0, // Always consider data stale to refetch on tab switch
  });
}

/**
 * Validator for video events (NIP-71)
 */
export function validateVideoEvent(event: NostrEvent): boolean {
  // Accept both current and legacy kinds
  if (![34235, 34236, 21, 22].includes(event.kind)) return false;

  // For addressable events (34235, 34236), require d tag
  if ([34235, 34236].includes(event.kind)) {
    const hasD = event.tags.some(([name]) => name === 'd');
    if (!hasD) return false;
  }

  // Must have a URL tag or imeta tag
  const hasUrl = event.tags.some(([name]) => name === 'url');
  const hasImeta = event.tags.some(([name]) => name === 'imeta');

  return hasUrl || hasImeta;
}

/**
 * Extract video URL from a video event
 */
export function extractVideoUrl(event: NostrEvent): string | null {
  // First try to find url tag
  const urlTag = event.tags.find(([name]) => name === 'url');
  if (urlTag && urlTag[1]) return urlTag[1];

  // Try imeta tag for legacy events
  const imetaTag = event.tags.find(([name]) => name === 'imeta');
  if (imetaTag) {
    const urlPart = imetaTag.find(part => part.startsWith('url '));
    if (urlPart) return urlPart.replace('url ', '');
  }

  return null;
}

/**
 * Extract video title from a video event
 */
export function extractVideoTitle(event: NostrEvent): string | null {
  const titleTag = event.tags.find(([name]) => name === 'title');
  return titleTag?.[1] || null;
}

/**
 * Extract thumbnail URL from a video event
 */
export function extractVideoThumbnail(event: NostrEvent): string | null {
  const thumbTag = event.tags.find(([name]) => name === 'thumb');
  if (thumbTag && thumbTag[1]) return thumbTag[1];

  const imageTag = event.tags.find(([name]) => name === 'image');
  return imageTag?.[1] || null;
}
